import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogService, ScreenSizeDetectorService, UiKitModule } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  accessAsset,
  accessGlobal,
  anyAsset,
  anyGlobal,
} from '@thehubz/client/core';
import { Asset, AssetDetails, AssetsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { timer } from 'rxjs';
import { AssetCommonService } from '../../../services/asset-common.service';
import { CollectionStepperDialogService } from '../../../services/collection-stepper-dialog.service';
import { SharingStepperDialogService } from '../../../services/sharing-stepper-dialog.service';
import { ActionButtonsComponent } from './action-buttons.component';

@Component({
  selector: 'thehubz-asset-gallery-card-actions',
  templateUrl: './asset-gallery-card-actions.component.html',
  styleUrls: ['./asset-gallery-card-actions.component.scss'],
  standalone: true,
  imports: [MatIconModule, MatMenuModule, CommonModule, MatTooltipModule, UiKitModule, ClientCoreModule, ActionButtonsComponent],
})
export class AssetGalleryCardActionsComponent implements AfterViewInit {
  @ViewChild('actions') actionTemplate: ElementRef<AnyType> | undefined;
  @Input() asset!: AssetUIModel;
  @Input() configObject!: AnyType;
  @Input() currentWindowSize!: AnyType;
  @Input() collectionOwner = false;
  @Input() collectionFlag = false;

  @Input() gridSettingsKeySuffix = '';
  @Input() assetIndex!: number;

  @Output() deleteAsset = new EventEmitter<{ asset: AssetUIModel; id: string; planDeletionFlag?: boolean }>();
  @Output() downloadAsset = new EventEmitter<{ asset: AssetUIModel; name: string }>();
  @Output() addRemoveFromBasket = new EventEmitter<AnyType>();
  @Output() editAsset = new EventEmitter<{ editType: string; assets: AssetUIModel[] }>();
  @Output() viewAsset = new EventEmitter<AssetUIModel>();
  @Output() editDraftAsset = new EventEmitter<{ assets: AssetUIModel[] }>();

  brandingKey!: CustomClients;
  AnyAsset = anyAsset;
  AnyGlobal = anyGlobal;
  actionsCount = 3;
  AccessAsset = accessAsset;
  AccessGlobal = accessGlobal;
  assetAspectRatio: AssetDetails[] = [];

  CustomClients = CustomClients;
  Status = Asset.StatusEnum;

  textAddToBasket = $localize`Add to basket`;
  textRemoveFromBasket = $localize`Remove from basket`;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    protected assetCommonService: AssetCommonService,
    private sharingStepperDialogService: SharingStepperDialogService,
    private assetService: AssetsApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private confirmDialog: ConfirmDialogService,
    protected collectionStepperDialogService: CollectionStepperDialogService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngAfterViewInit() {
    timer(0).subscribe(() => {
      this.actionsCount = this.actionTemplate?.nativeElement.children.length;
    });
  }

  async shareAsset(asset: AssetUIModel) {
    let assetData = asset;
    const assetID = asset._id;
    const aspectRatioIDs = this.assetAspectRatio.map((asset: AssetDetails) => asset.assetId);
    const assetIDx = aspectRatioIDs.findIndex((id: AnyType) => id === assetID);
    if (assetIDx == -1) assetData = await this.getAssetDetail(assetID);
    if (assetData) {
      const stepperData = await this.sharingStepperDialogService.createStepperModel(
        this.assetAspectRatio,
        `Share assets: <span class="font-light">${this.asset.name}<span>`,
      );
      this.sharingStepperDialogService.openSharingDialog(stepperData);
    }
  }

  editAssets(editType: string) {
    const selectedAssets = this.asset;
    const assetObject = {
      editType: editType,
      assets: [selectedAssets],
    };
    this.editAsset.emit(assetObject);
  }

  updateDraftAsset() {
    const selectedAssets = this.asset;
    const assetObject = {
      assets: [selectedAssets],
    };
    this.editDraftAsset.emit(assetObject);
  }

  private async getAssetDetail(assetID: string) {
    return new Promise<any>((resolve, reject) => {
      this.assetService.assetsControllerFindOne(assetID).subscribe({
        next: async (asset) => {
          if (asset) {
            await this.setAssetDetails(asset);
            resolve(asset);
          }
        },
        error: (err) => {
          this.clientNotificationHandlerService.errorNotification(err);
          reject(err);
        },
      });
    });
  }

  async setAssetDetails(assetObj: Asset) {
    if (assetObj.currentVersion?.aspectRatios) {
      this.assetAspectRatio.push({
        assetId: assetObj._id,
        name: assetObj.name,
        version: assetObj.currentVersion?.version || 0,
        aspectRatios: assetObj.currentVersion?.aspectRatios || [],
      });
    }
  }
  addToCollection() {
    this.collectionStepperDialogService.openDialogForCollection([this.asset]);
  }

  removeFromCollection(assetID: string) {
    this.confirmDialog
      .open({
        title: $localize`Remove asset`,
        message: $localize`Are you sure you want to remove this asset from the collection?`,
      })
      .subscribe((confirm: boolean) => {
        if (confirm) {
          this.assetCommonService.collectionRemoveAsset$.next([{ assetID: assetID }]);
        }
      });
  }
}
